import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  cancelOutgoingTransfer,
  enrichProjectsWithHoldingsProjectFields,
  fetchPlatformSettings,
  fetchPortfolio,
  fetchRedemptionHistory,
  fetchTransferHistory,
  listProjects,
  unwrapListPayload,
  unwrapRedemptionHistoryPayload,
  unwrapTransferHistoryPayload,
} from "../services/investmentService";
import IncomingTransfersAcceptCard from "../components/investor/IncomingTransfersAcceptCard";
import InvestorConfirmDialog from "../components/investor/InvestorConfirmDialog";
import InvestorHistoryFilters from "../components/investor/InvestorHistoryFilters";
import InvestorMetricGrid from "../components/investor/InvestorMetricGrid";
import InvestorPageHeader from "../components/investor/InvestorPageHeader";
import InvestorSectionHeading from "../components/investor/InvestorSectionHeading";
import SharesManagementMarketTabs from "../components/investor/SharesManagementMarketTabs";
import ShareBalanceBar from "../components/investor/ShareBalanceBar";
import TransferOfferDetailModal from "../components/investor/TransferOfferDetailModal";
import TransferSharesRequestModal from "../components/investor/TransferSharesRequestModal";
import { TRANSFERABLE_PROJECTS_LABEL } from "../utils/investorNavConfig";
import { formatShareQty, isInvestorTransferEligible } from "../utils/investorRedeemEligibility";
import { transferableSharesFromHolding } from "../utils/investorTransferShares";
import { formatApiDetail } from "../utils/transferApiErrors";
import {
  canCancelOutgoingTransfer,
  formatMmSs,
  msUntilOutgoingTransferCancel,
} from "../utils/secondaryTransferCancel";
import { transferStatusLabel, transferStatusTone } from "../utils/transferLabels";
import {
  buildLifecycleBuckets,
  filterInvestorCatalogProjects,
  isCompletedProjectStatus,
  projectTitleWithCode,
} from "../utils/projectViews";

const shareFmt = new Intl.NumberFormat("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatShare = (n) => shareFmt.format(Number(n || 0));

const DEFAULT_FILTERS = {
  project_id: "",
  status: "",
  date_from: "",
  date_to: "",
  q: "",
  sort: "newest",
  page: 1,
};

export default function TransferableProjectsPage() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [count, setCount] = useState(0);
  const [portfolio, setPortfolio] = useState([]);
  const [holdingsByProject, setHoldingsByProject] = useState(() => new Map());
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [detailRow, setDetailRow] = useState(null);
  const [cancellingId, setCancellingId] = useState("");
  const [transferModalHolding, setTransferModalHolding] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [cancelConfirmRow, setCancelConfirmRow] = useState(null);
  const [marketTabCounts, setMarketTabCounts] = useState({
    fundraising: 0,
    funded: 0,
    transferable: 0,
    redeemPending: 0,
  });
  const [clock, setClock] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setClock((x) => x + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const [portfolioLoading, setPortfolioLoading] = useState(true);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setMessage("");
    try {
      const params = { page: filters.page, page_size: 25, sort: filters.sort };
      if (filters.project_id) params.project_id = filters.project_id;
      if (filters.status) params.status = filters.status;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.q) params.q = filters.q;
      const { data: hist } = await fetchTransferHistory(params);
      const unpacked = unwrapTransferHistoryPayload(hist);
      setRows(unpacked.rows);
      setSummary(unpacked.summary);
      setCount(unpacked.count);
    } catch (err) {
      const status = err?.response?.status;
      let hint = formatApiDetail(err, "Failed to load transfer history.");
      if (status === 400 && String(hint).toLowerCase().includes("project_id")) {
        hint =
          "Transfer history API is out of date. Restart the backend server, then run: python manage.py migrate investments";
      }
      setMessage(hint);
      setRows([]);
      setSummary(null);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    let cancelled = false;
    setPortfolioLoading(true);
    Promise.all([
      listProjects(),
      fetchPlatformSettings(),
      fetchPortfolio(),
      fetchRedemptionHistory({ status: "PENDING", page_size: 1, page: 1 }).catch(() => ({ data: null })),
    ])
      .then(([projectsRes, settingsRes, portfolioRes, redeemPendingRes]) => {
        if (cancelled) return;
        const holdings = unwrapListPayload(portfolioRes.data);
        const projects = filterInvestorCatalogProjects(
          enrichProjectsWithHoldingsProjectFields(unwrapListPayload(projectsRes.data), holdings)
        );
        const buckets = buildLifecycleBuckets(projects, settingsRes.data);
        const map = new Map();
        for (const h of holdings) {
          const id = h.project?.id;
          if (id == null || id === "") continue;
          map.set(String(id), { holding: h, project: h.project });
        }
        for (const p of projects) {
          const id = String(p.id);
          if (map.has(id)) map.get(id).project = p;
        }
        setPortfolio(holdings);
        setHoldingsByProject(map);
        const transferable = holdings.filter((h) => {
          const entry = map.get(String(h.project?.id));
          return entry && isInvestorTransferEligible(entry.holding, entry.project);
        });
        const redeemPayload = unwrapRedemptionHistoryPayload(redeemPendingRes?.data);
        setMarketTabCounts({
          fundraising: buckets.fundraising.length,
          funded: buckets.funded.filter((p) => !isCompletedProjectStatus(p.status)).length,
          transferable: transferable.length,
          redeemPending: Number(redeemPayload.summary?.pending_count ?? 0),
        });
      })
      .catch(() => {
        if (!cancelled) setMessage("Failed to load portfolio.");
      })
      .finally(() => {
        if (!cancelled) setPortfolioLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const projectOptions = useMemo(() => {
    const map = new Map();
    for (const h of portfolio) {
      const id = h.project?.id;
      if (!id) continue;
      const p = holdingsByProject.get(String(id))?.project || h.project;
      const label = projectTitleWithCode(p) || id;
      map.set(String(id), label);
    }
    for (const r of rows) {
      if (r.project && r.project_display) map.set(String(r.project), r.project_display);
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [portfolio, holdingsByProject, rows]);

  const transferableHoldings = useMemo(
    () =>
      portfolio.filter((h) => {
        const entry = holdingsByProject.get(String(h.project?.id));
        if (!entry) return false;
        return isInvestorTransferEligible(entry.holding, entry.project);
      }),
    [portfolio, holdingsByProject]
  );

  const totals = useMemo(() => {
    let locked = 0;
    let available = 0;
    for (const h of transferableHoldings) {
      locked += Number(h.shares_pending_redemption || 0);
      available += transferableSharesFromHolding(h);
    }
    return { locked, available };
  }, [transferableHoldings]);

  const holdingForModal = useMemo(() => {
    if (!transferModalHolding) return null;
    const pid = String(transferModalHolding.project?.id || "");
    const entry = holdingsByProject.get(pid);
    const h = entry?.holding || transferModalHolding;
    const p = entry?.project || h.project;
    return {
      project_id: pid,
      project_code: p?.project_code,
      project_name: p?.name,
      shares_held: h.shares_held,
      shares_pending_redemption: h.shares_pending_redemption,
      shares_redeemed_total: h.shares_redeemed_total,
      project: p,
    };
  }, [transferModalHolding, holdingsByProject]);

  const executeCancelTransfer = async (row) => {
    if (!row?.id || cancellingId) return;
    setCancellingId(row.id);
    setMessage("");
    try {
      await cancelOutgoingTransfer(row.id);
      setCancelConfirmRow(null);
      setSuccessMsg(
        row.transfer_ref
          ? `Transfer ${row.transfer_ref} cancelled.`
          : "Transfer offer cancelled."
      );
      await loadHistory();
    } catch (err) {
      setMessage(formatApiDetail(err, "Could not cancel offer."));
    } finally {
      setCancellingId("");
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / 25));

  return (
    <div className="page investor-panel investor-panel-enhanced">
      <p className="wallet-breadcrumb investor-module-breadcrumb">
        <Link to="/investments">Shares Management</Link>
        <span aria-hidden="true"> / </span>
        <span>{TRANSFERABLE_PROJECTS_LABEL}</span>
      </p>
      <InvestorPageHeader
        icon="transfer"
        title={TRANSFERABLE_PROJECTS_LABEL}
        subtitle="Track transfer offers, locked shares, and settlement — same workflow as on project cards."
      />
      <div className="invx-filters card" style={{ marginBottom: 16 }}>
        <SharesManagementMarketTabs activeView="fundraising" onViewChange={() => {}} counts={marketTabCounts} />
      </div>
      <IncomingTransfersAcceptCard
        onAccepted={() => {
          loadHistory();
          setSuccessMsg("Transfer accepted. Your holdings and wallet are updated.");
        }}
        onError={setMessage}
      />
      {successMsg ? <p className="msg msg-success">{successMsg}</p> : null}
      {message ? <p className="msg msg-error">{message}</p> : null}

      <InvestorMetricGrid
        metrics={[
          { key: "p", label: "Pending offers", value: String(summary?.pending_count ?? 0) },
          { key: "l", label: "Shares locked", value: formatShare(totals.locked) },
          {
            key: "a",
            label: "Available to transfer",
            value: formatShare(totals.available),
            highlight: true,
          },
          { key: "t", label: "Lifetime transferred", value: formatShare(summary?.fulfilled_shares ?? 0) },
        ]}
      />

      <section className="investor-section">
        <InvestorSectionHeading icon="transfer-eligible">Transfer-eligible projects</InvestorSectionHeading>
        <p className="investor-section__desc muted" style={{ marginTop: 0 }}>
          Use <strong>Transfer Shares</strong> to send an NAV-locked offer. The recipient must accept before shares
          move. Locked redemption requests reduce available shares shown here.
        </p>
        {portfolioLoading && transferableHoldings.length === 0 ? (
          <p className="muted">Loading…</p>
        ) : transferableHoldings.length === 0 ? (
          <p className="muted">No transferable holdings right now.</p>
        ) : (
          <ul className="redeem-eligible-list">
            {transferableHoldings.map((h) => {
              const p = holdingsByProject.get(String(h.project?.id))?.project || h.project;
              const label = projectTitleWithCode(p) || "Project";
              const held = Number(h.shares_held || 0);
              const pending = Number(h.shares_pending_redemption || 0);
              const transferableQty = transferableSharesFromHolding(h);
              return (
                <li key={h.project?.id || label} className="redeem-eligible-list__item">
                  <div className="redeem-eligible-list__head">
                    <strong className="redeem-eligible-list__title">{label}</strong>
                    <button
                      type="button"
                      className="btn-secondary compact-btn redeem-eligible-list__btn"
                      onClick={() => setTransferModalHolding(h)}
                    >
                      Transfer Shares
                    </button>
                  </div>
                  <ShareBalanceBar held={held} locked={pending} available={transferableQty} redeemed={h.shares_redeemed_total} />
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <InvestorHistoryFilters
        idPrefix="transfer"
        variant="transfer"
        filters={filters}
        onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
        projectOptions={projectOptions}
        onApply={loadHistory}
        onReset={() => setFilters(DEFAULT_FILTERS)}
        loading={loading}
      />

      <section className="investor-section">
        <div className="investor-section__head">
          <div>
            <InvestorSectionHeading icon="offer-history">Offer history</InvestorSectionHeading>
            <p className="investor-section__desc">
              Every transfer you sent. Open <strong>Details</strong> for a full breakdown. Cancel pending offers after 5
              minutes if not yet accepted.
            </p>
          </div>
        </div>
        <div className="investor-history-toolbar">
          <span className="investor-history-toolbar__count">
            {loading ? "Loading…" : `${count} record${count === 1 ? "" : "s"}`}
            {summary?.pending_count > 0 ? ` · ${summary.pending_count} pending` : ""}
          </span>
        </div>
        {Number(summary?.pending_count ?? 0) > 0 && filters.status !== "PENDING" ? (
          <div className="investor-pending-banner" role="status">
            <span>
              {summary.pending_count} pending offer{summary.pending_count === 1 ? "" : "s"} not shown in this filter.
            </span>
            <button
              type="button"
              className="btn-secondary compact-btn"
              onClick={() => setFilters((f) => ({ ...f, status: "PENDING", page: 1 }))}
            >
              Show pending only
            </button>
          </div>
        ) : null}
        <div className="table-wrap table-wrap--cards">
          <table className="ops-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Project</th>
                <th>Transfer ID</th>
                <th>Recipient</th>
                <th>Offered</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="muted">
                    {loading ? "Loading…" : "No transfer records match your filters."}
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => {
                  const waitMs = msUntilOutgoingTransferCancel(row.requested_at);
                  const cancelReady = canCancelOutgoingTransfer(row);
                  return (
                    <tr key={row.id}>
                      <td data-label="#">{(filters.page - 1) * 25 + idx + 1}</td>
                      <td data-label="Project">{row.project_display || "—"}</td>
                      <td data-label="ID">{row.transfer_ref || "—"}</td>
                      <td data-label="Recipient">{row.transfer_recipient_display || "—"}</td>
                      <td data-label="Offered">{formatShare(row.shares)}</td>
                      <td data-label="Status">
                        <span className={`badge ${transferStatusTone(row.status)}`}>{transferStatusLabel(row.status)}</span>
                      </td>
                      <td data-label="Actions">
                        <span className="investor-history-actions">
                          <button type="button" className="btn-secondary compact-btn" onClick={() => setDetailRow(row)}>
                            Details
                          </button>
                          {String(row.status).toUpperCase() === "PENDING" ? (
                            <button
                              type="button"
                              className="btn-danger compact-btn"
                              disabled={cancellingId === row.id || !cancelReady}
                              title={
                                cancelReady
                                  ? "Withdraw this offer"
                                  : `Cancel in ${formatMmSs(waitMs)}`
                              }
                              onClick={() => setCancelConfirmRow(row)}
                            >
                              Cancel
                            </button>
                          ) : null}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 ? (
          <div className="investor-history-pagination">
            <button
              type="button"
              className="btn-secondary compact-btn"
              disabled={filters.page <= 1}
              onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
            >
              Previous
            </button>
            <span className="muted">
              Page {filters.page} of {totalPages}
            </span>
            <button
              type="button"
              className="btn-secondary compact-btn"
              disabled={filters.page >= totalPages}
              onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
            >
              Next
            </button>
          </div>
        ) : null}
      </section>

      <InvestorConfirmDialog
        open={Boolean(cancelConfirmRow)}
        title="Cancel transfer offer?"
        message={
          cancelConfirmRow ? (
            <>
              Cancel transfer <strong>{cancelConfirmRow.transfer_ref || "this offer"}</strong>? The recipient will no
              longer see this offer.
            </>
          ) : (
            ""
          )
        }
        confirmLabel="Confirm cancel"
        cancelLabel="Keep offer"
        variant="danger"
        busy={Boolean(cancelConfirmRow && cancellingId === cancelConfirmRow.id)}
        onCancel={() => !cancellingId && setCancelConfirmRow(null)}
        onConfirm={() => cancelConfirmRow && executeCancelTransfer(cancelConfirmRow)}
      />

      {transferModalHolding && holdingForModal ? (
        <TransferSharesRequestModal
          holding={holdingForModal}
          onClose={() => setTransferModalHolding(null)}
          onSuccess={() => {
            loadHistory();
          }}
        />
      ) : null}

      <TransferOfferDetailModal
        row={detailRow}
        onClose={() => setDetailRow(null)}
        onRequestCancel={(row) => {
          setDetailRow(null);
          setCancelConfirmRow(row);
        }}
        cancelling={Boolean(detailRow && cancellingId === detailRow.id)}
      />
    </div>
  );
}
